import {
  PreviewHoverPayload,
  InspectorHoverMatch,
  ColorField,
} from 'types/apis/cxEditor';

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

/** Convert any CSS color string coming from computed style into #rrggbb. */
export function toHex(color: string): string | null {
  if (!color) return null;
  const s = color.trim().toLowerCase();

  if (s === 'transparent') return null;

  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  }
  if (/^#[0-9a-f]{8}$/i.test(s)) {
    const alpha = parseInt(s.slice(7, 9), 16);
    return alpha === 0 ? null : s.slice(0, 7).toLowerCase();
  }

  const rgbMatch = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
  if (rgbMatch) {
    const alpha = rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]);
    if (!Number.isFinite(alpha) || alpha === 0) return null;
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  return null;
}

function colorDistanceSq(hex1: string, hex2: string): number {
  const parse = (h: string) => ({
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  });
  const a = parse(hex1);
  const b = parse(hex2);
  return (a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2;
}

// ---------------------------------------------------------------------------
// Color candidate index
// ---------------------------------------------------------------------------

export type ColorCandidate = {
  id: string;
  hex: string; // normalized #rrggbb
};

export function buildColorIndex(fields: ColorField[]): ColorCandidate[] {
  const out: ColorCandidate[] = [];
  for (const f of fields) {
    const hex = toHex(f.value);
    if (hex) out.push({ id: f.id, hex });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Matching — data-attribute IDs only, no value inspection
// ---------------------------------------------------------------------------

/**
 * Resolve hover payload to inspector field IDs purely via data-attribute keys.
 * No text/value matching of any kind.
 *
 * Text/image/metadata fields: data-configs (non-palette) + data-strings → directMatchIds
 * Color fields: data-configs (palette) + data-colors → colorMatch, fallback to nearest hex
 */
export function computeHoverMatch(
  payload: PreviewHoverPayload | null,
  colorIndex: ColorCandidate[],
): InspectorHoverMatch {
  const empty: InspectorHoverMatch = { colorMatch: null, bgColorMatch: null, directMatchIds: null };
  if (!payload) return empty;

  let colorMatch: InspectorHoverMatch['colorMatch'] = null;
  let directMatchIds: string[] | null = null;

  // --- Data-attribute direct match ---
  const hints = payload.dataAttrHints;
  if (hints) {
    const candidateTextIds = [...hints.textIds, ...hints.stringIds];
    if (candidateTextIds.length > 0) {
      directMatchIds = candidateTextIds;
    }

    const colorIdSet = new Set(colorIndex.map(c => c.id));
    const directColorId = hints.colorIds.find(id => colorIdSet.has(id));
    if (directColorId) colorMatch = { id: directColorId, score: 100, reason: 'direct' };
  }

  // --- Color hex fallback (when no color data-attr) ---
  if (!colorMatch && payload.colorHex) {
    const hoverHex = toHex(payload.colorHex);
    if (hoverHex) {
      const exactColor = colorIndex.find(c => c.hex === hoverHex);
      if (exactColor) {
        colorMatch = { id: exactColor.id, score: 100, reason: 'exact-hex' };
      } else if (colorIndex.length > 0) {
        let best = { id: '', dist: Infinity };
        for (const c of colorIndex) {
          const d = colorDistanceSq(hoverHex, c.hex);
          if (d < best.dist) best = { id: c.id, dist: d };
        }
        if (best.dist <= 500 && best.id) {
          colorMatch = { id: best.id, score: 70, reason: 'near-hex' };
        }
      }
    }
  }

  // --- Background color ---
  let bgColorMatch: InspectorHoverMatch['bgColorMatch'] = null;
  if (payload.backgroundHex) {
    const bgHex = toHex(payload.backgroundHex);
    if (bgHex) {
      const exactBg = colorIndex.find(c => c.hex === bgHex);
      if (exactBg && exactBg.id !== colorMatch?.id) {
        bgColorMatch = { id: exactBg.id, score: 100, reason: 'exact-hex' };
      } else if (!exactBg && colorIndex.length > 0) {
        let best = { id: '', dist: Infinity };
        for (const c of colorIndex) {
          if (c.id === colorMatch?.id) continue;
          const d = colorDistanceSq(bgHex, c.hex);
          if (d < best.dist) best = { id: c.id, dist: d };
        }
        if (best.dist <= 500 && best.id) {
          bgColorMatch = { id: best.id, score: 70, reason: 'near-hex' };
        }
      }
    }
  }

  return { colorMatch, bgColorMatch, directMatchIds };
}
